import { KeyValue } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import { FormErrorEnum } from '@shared/client/models/form.model';
import { ByteFormatPipe } from '@shared/client/pipes/byte-format.pipe';

@Injectable({
    providedIn: 'root',
})
export class FormService {
    public static acceptedImageExtensions = ['.jpeg', '.jpg', '.png'];
    public static FORBIDDEN_CHARS = ['[', ']', ';', '{', '}', '"'];
    public static MAX_POSTGRESQL_IDENTIFIER_NAME = 63;
    public static maxAssignmentsLimitExceeded = 1000;
    /**
     * @description Value in bytes (~2GB)
     */
    public static maxFileSize = 2147483648;
    /**
     * @description Value in bytes (200kB)
     */
    public static maxImageSize = 204800;
    public static RESERVED_WORD = new Set([
        'alter',
        'and',
        'as',
        'check',
        'column',
        'constraint',
        'create',
        'database',
        'delete',
        'distinct',
        'drop',
        'false',
        'foreign',
        'from',
        'full',
        'grant',
        'group',
        'index',
        'inner',
        'insert',
        'into',
        'join',
        'key',
        'left',
        'limit',
        'not',
        'null',
        'offset',
        'on',
        'or',
        'order',
        'primary',
        'references',
        'returning',
        'revoke',
        'right',
        'role',
        'schema',
        'select',
        'table',
        'true',
        'truncate',
        'unique',
        'update',
        'user',
        'uuid',
        'values',
        'view',
        'where',
    ]);
    public static VARCHAR_COMMON_LENGTH = 255;
    public static VARCHAR_LARGE_LENGTH = 1023;
    public static VARCHAR_MEDIUM_LENGTH = 100;
    public static VARCHAR_MIN_LENGTH = 2;
    public static VARCHAR_SMALL_LENGTH = 50;

    private readonly byteFormatPipe = inject(ByteFormatPipe);
    private readonly translocoService = inject(TranslocoService);

    public getErrorMessage(error: KeyValue<string, ValidationErrors>): string {
        switch (error.key) {
            case FormErrorEnum.CANNOT_START_WITH_NUMBER:
                return this.translocoService.translate('cannotStartWithNumber');
            case FormErrorEnum.INVALID_CHARACTERS:
                return this.translocoService.translate('invalidCharacters', {
                    chars: error.value['forbiddenChars'].join('  '),
                });
            case FormErrorEnum.MAX_ASSIGNMENTS_LIMIT_EXCEEDED:
                return this.translocoService.translate('maxAssignmentsLimitExceeded');
            case FormErrorEnum.EMAIL:
                return this.translocoService.translate('mailAddressErrorMessage');
            case FormErrorEnum.EMAIL_ALREADY_USED:
                return this.translocoService.translate('emailAlreadyUsedErrorMessage');
            case FormErrorEnum.FORBIDDEN_WORD:
                return this.translocoService.translate('forbiddenWordErrorMessage', { word: error.value['word'] });
            case FormErrorEnum.ONLY_UNACCENT_AND_ALPHANUMERIC:
                return this.translocoService.translate('permittedCharacters', {
                    characters: ['a-z, 0-9', ...(error.value['exceptions'] ?? [])].join(', '),
                });
            case FormErrorEnum.MAX:
                return this.translocoService.translate('maxValue', { max: error.value['max'] });
            case FormErrorEnum.MIN:
                return this.translocoService.translate('minValue', { min: error.value['min'] });
            case FormErrorEnum.MAX_FILE_SIZE:
                return this.translocoService.translate('maxFileSize', {
                    value: this.byteFormatPipe.transform(FormService.maxFileSize),
                });
            case FormErrorEnum.MAX_IMAGE_SIZE:
                return this.translocoService.translate('maxImageSize', {
                    value: this.byteFormatPipe.transform(FormService.maxImageSize),
                });
            case FormErrorEnum.MAX_LENGTH:
                return this.translocoService.translate('maxLengthErrorMessage');
            case FormErrorEnum.MIN_LENGTH:
                return this.translocoService.translate('minLengthErrorMessage', {
                    minLength: error.value['requiredLength'],
                });
            case FormErrorEnum.MISMATCH:
                return this.translocoService.translate('mismatchPasswordErrorMessage');
            case FormErrorEnum.MISSING_NUMBER:
                return this.translocoService.translate('missingNumberErrorMessage', { COUNT: error.value });
            case FormErrorEnum.MISSING_SPECIAL_CHARACTER:
                return this.translocoService.translate('missingSpecialCharacterErrorMessage', { COUNT: error.value });
            case FormErrorEnum.MISSING_UPPERCASE_CHARACTER:
                return this.translocoService.translate('missingUppercaseCharacterErrorMessage', { COUNT: error.value });
            case FormErrorEnum.REQUIRED:
                return this.translocoService.translate('requiredField');
            case FormErrorEnum.UNSUPPORTED_FILETYPE:
                return this.translocoService.translate('unsupportedFileTypeErrorMessage');
            case FormErrorEnum.VALIDATE_DATE:
                return this.translocoService.translate('endDateBigger');
            default:
                return this.translocoService.translate('invalidField');
        }
    }
}
